<script setup lang="ts">
import type { NuxtError } from "#app";

const props = defineProps<{
  error: NuxtError & {
    stack?: string;
    cause?: unknown;
    data?: unknown;
  };
}>();

const isDev = import.meta.dev;
const route = useRoute();

const errorTitle = computed(() => props.error.statusMessage || props.error.message || "Unexpected error");
const statusCode = computed(() => props.error.statusCode || 500);
const stack = computed(() => (isDev ? props.error.stack?.trim() || "" : ""));
const retryPath = computed(() => route.fullPath || "/");
const extraData = computed(() => {
  if (!isDev) {
    return "";
  }

  if (props.error.data) {
    return JSON.stringify(props.error.data, null, 2);
  }

  if (props.error.cause) {
    return JSON.stringify(props.error.cause, null, 2);
  }

  return "";
});
</script>

<template>
  <main class="mx-auto max-w-4xl px-4 py-10 md:py-16">
    <section class="rounded-[28px] border border-red-200 bg-[linear-gradient(135deg,_#fff7f7_0%,_#ffffff_52%,_#fff1f2_100%)] p-6 shadow-[0_20px_60px_-40px_rgba(220,38,38,0.35)] dark:border-red-900 dark:bg-[linear-gradient(135deg,_#111827_0%,_#0f172a_55%,_#1f2937_100%)] md:p-8">
      <p class="text-xs font-semibold uppercase tracking-[0.22em] text-red-700 dark:text-red-300">
        Error {{ statusCode }}
      </p>
      <h1 class="mt-3 text-3xl font-black tracking-tight text-slate-900 dark:text-white">
        {{ errorTitle }}
      </h1>
      <p class="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
        <span v-if="isDev">
          Local debug mode is on. The details below should help identify the exact failing route or server code.
        </span>
        <span v-else>
          Something went wrong while loading this page.
        </span>
      </p>

      <div v-if="isDev && stack" class="mt-6">
        <h2 class="text-sm font-semibold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-300">
          Stack Trace
        </h2>
        <pre class="mt-2 overflow-x-auto rounded-2xl border border-slate-300 bg-slate-950 p-4 text-xs leading-6 text-slate-100 dark:border-slate-700">{{ stack }}</pre>
      </div>

      <div v-if="isDev && extraData" class="mt-6">
        <h2 class="text-sm font-semibold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-300">
          Error Data
        </h2>
        <pre class="mt-2 overflow-x-auto rounded-2xl border border-slate-300 bg-slate-950 p-4 text-xs leading-6 text-slate-100 dark:border-slate-700">{{ extraData }}</pre>
      </div>

      <div class="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          class="rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-500"
          @click="clearError({ redirect: '/' })"
        >
          Go Home
        </button>
        <button
          v-if="isDev"
          type="button"
          class="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-red-300 hover:text-red-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          @click="clearError({ redirect: retryPath })"
        >
          Retry Route
        </button>
      </div>
    </section>
  </main>
</template>
